import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "./GlassButtons";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { GlassInputControl, GlassInputControlLabel, GlassInputGroup, GlassInputGroupTitle } from "./GlassInput";
import { SelectLayer } from "./CommonInputs";
import { useContext, useEffect, useMemo, useState } from "react";
import { MapitStateActionType, MapitStateContext } from "src/states/MapitState";
import { LayerInfo, LayerVariabilityType } from "src/common/managers/Types";
import { bbox, buffer } from "@turf/turf";
import { GeoJSONSource } from "maplibre-gl";
import { DataCollector } from "src/dataCollector/DataCollector";
import { Persistence } from "src/managers/Persistence";
import { ProtectedFeature } from "./ProtectedFeature";
import { Feature } from "src/states/ApplicationState";
import { Spinner } from "react-bootstrap";
import { ViewButtonText } from "src/propertyInfoTemplates/PropertyComponents";
import { wfsEndpoints } from "src/dataCollector/WFSCollectorEndpoints";

export function ConflictSearchDialog({ window }: { window: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const [selectedLayer, setSelectedLayerId] = useState<number | undefined>(undefined);
   const [bufferDistance, setBufferDistance] = useState<number>(0);
   const [foundFeatures, setFoundFeatures] = useState<any>(null);
   const [endpoint, setEndpoint] = useState<keyof typeof wfsEndpoints>("Matriklen2");
   const [isDownloading, setIsDownloading] = useState<boolean>(false);
   const [whoAmI, setWhoAmI] = useState<string | null>(null);
   const [error, setError] = useState<string | null>(null);
   const [featureType, setFeatureType] = useState<keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"] | undefined>();

   useEffect(() => {
      setFeatureType(Object.keys(wfsEndpoints[endpoint]?.featureTypes ?? {})[0] as keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"]);
   }, [endpoint]);

   const selectableLayers = useMemo(() => {
      let options = Object.values(mapitState.layers).filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer);
      setSelectedLayerId((prev) => prev ? prev : options.length > 0 ? options[0].layerId : undefined);
      return options;
   }, [mapitState.layers])

   useEffect(() => {
      if (mapitState.map) {
         let map = mapitState.map.getMapPop();
         addFill(map, "conflict-search-area", "#FF0000");
         addOutline(map, "found-features", "#0000FF");
         addPoint(map, "found-features", "#0000FF");
         return () => {
            removeLayer(map, "conflict-search-area");
            removeLayer(map, "found-features");
         }
      }
   }, [mapitState.map]);

   useEffect(() => {
      if (mapitState.map && selectedLayer) {
         let geom = mapitState.layers[selectedLayer]?.geoJson;
         let map = mapitState.map.getMapPop();
         setGeom(map, "conflict-search-area", buffer(geom, bufferDistance || 0, { units: 'meters' }));
      }
   }, [mapitState.map, selectedLayer, bufferDistance]);

   useEffect(() => {
      if (mapitState.map && foundFeatures) {
         let geom = foundFeatures;
         let map = mapitState.map.getMapPop();
         setGeom(map, "found-features", geom);
      }
   }, [mapitState.map, foundFeatures]);

   return (
      <AdvancedDragModal
         PosDefault={{ left: "60px", top: "60px", width: "350px", height: "550px" }}
         title={Localization.getText("Conflict search")}
         onClose={() => windowDispatch(closeWindow(window.id))}
         subHeaderContent={<ViewButtonText disabled style={{ background: "orange", pointerEvents: "none", marginRight: "auto" }} > Early preview ⁉️</ViewButtonText>}
      >
         <ADModalBody>
            <GlassInputGroup >
               <GlassInputGroupTitle>{Localization.getText("Search area")}</GlassInputGroupTitle>
               <GlassInputControlLabel>{""}</GlassInputControlLabel>
               <SelectLayer selectedLayerId={selectedLayer || ""} onSelect={(e) => { setSelectedLayerId(e); }} availableLayers={selectableLayers} />
               <GlassInputControlLabel>{Localization.getText("Buffer")}</GlassInputControlLabel>
               <GlassInputControl><input type="number" value={bufferDistance} onChange={(e) => setBufferDistance(Number(e.target.value))} /><span>m</span></GlassInputControl>


               <GlassInputGroupTitle>{Localization.getText("Datasource")}</GlassInputGroupTitle>
               <GlassInputControlLabel>{""}</GlassInputControlLabel>
               <GlassInputControl>
                  <select value={endpoint} onChange={(e) => setEndpoint(e.target.value as keyof typeof wfsEndpoints)}>
                     {Object.keys(wfsEndpoints).filter((a) => wfsEndpoints[a].active !== false).map((key) => (
                        <option key={key} value={key}>{key}</option>
                     ))}
                  </select>
               </GlassInputControl>
               {wfsEndpoints[endpoint]?.["whoAmI"] ?
                  <>
                     <GlassInputControlLabel>{Localization.getText("Email")}</GlassInputControlLabel>
                     <GlassInputControl>
                        <input type="email" value={whoAmI ?? ""} onChange={(e) => setWhoAmI(e.target.value)} />
                     </GlassInputControl>
                  </>
                  : <></>
               }


               <GlassInputControlLabel>{Localization.getText("Layer")}</GlassInputControlLabel>
               <GlassInputControl>
                  <select value={featureType} onChange={(e) => setFeatureType(e.target.value as keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"])}>
                     {Object.keys(wfsEndpoints[endpoint]?.featureTypes ?? {}).map((key) => (
                        <option key={key} value={key}>{key}</option>
                     ))}
                  </select>
               </GlassInputControl>
               <ProtectedFeature feature={Feature.Debugging} contentsIfNoAccess={<></>} >
                  {SettingsManager.getSystemSetting("debuggingShowExtraDebugging") ?
                     <>
                        <GlassInputControlLabel>Debug</GlassInputControlLabel>
                        <GlassButtonRow wrap={true}>
                           <GlassButton disabled={!endpoint} onClick={async () => (console.log(await DataCollector.WFS.getCapabilities(endpoint)))} >
                              GetCapabilities
                           </GlassButton>
                           <GlassButton disabled={!featureType} onClick={async () => {
                              await DataCollector.WFS.describeFeatureType(endpoint, featureType!);
                           }} >
                              DescribeFeatureType
                           </GlassButton>
                           <GlassButton disabled={!selectedLayer || !featureType} onClick={async () => {
                              if (selectedLayer) {
                                 let x = buffer(mapitState.layers[selectedLayer]?.geoJson, bufferDistance || 0, { units: 'meters' });
                                 if (x) {
                                    setFoundFeatures(
                                       await DataCollector.WFS.getFeatureBBOX(endpoint, featureType!, bbox(x).flat() as [number, number, number, number])
                                    );
                                 }
                              }
                           }} >
                              GetFeature BBOX
                           </GlassButton>

                        </GlassButtonRow>
                     </> : <></>}
               </ProtectedFeature>
               <GlassInputControlLabel>{Localization.getText("Action")}</GlassInputControlLabel>
               <GlassButton disabled={!selectedLayer || !featureType} onClick={async () => {
                  if (selectedLayer) {
                     let x = mapitState.layers[selectedLayer]?.geoJson;
                     if (bufferDistance) {
                        x = buffer(x, bufferDistance, { units: 'meters' });
                     }
                     if (x) {
                        try {
                           setIsDownloading(true);
                           setError(null);
                           setFoundFeatures(null);
                           DataCollector.WFS.getFeatureInsidePolygon(endpoint, featureType!, x as any, {
                              whoami: wfsEndpoints[endpoint]?.["whoAmI"] ? (whoAmI ?? undefined) : undefined
                           }).then((result) => {
                              setIsDownloading(false);
                              setFoundFeatures(result);
                           }).catch((err) => {
                              setIsDownloading(false);
                              console.error(err);
                              setError(err.message);
                           })
                        } catch (e) {
                           setError((e as Error).message);
                           return;
                        }
                     }
                  }
               }} >
                  {Localization.getText("Fetch")}
               </GlassButton>
               {
                  isDownloading ?
                     <>
                        <GlassInputControlLabel top>{Localization.getText("Status")}</GlassInputControlLabel>
                        <Spinner animation="border" role="status" size="sm" style={{ marginLeft: 10 }} />
                     </>
                     : <></>
               }
               {
                  error ?
                     <>
                        <GlassInputControlLabel top>{"Error"}</GlassInputControlLabel>
                        <GlassInputControl>
                           <span style={{ color: "red" }}>{Localization.getTextSafeMode(error)}</span>
                        </GlassInputControl>
                     </>
                     : <></>
               }
               {
                  foundFeatures ?
                     <>
                        <GlassInputControlLabel top >{"Info"}</GlassInputControlLabel>
                        <GlassInputControl>
                           <table width="100%">
                              <tbody>
                                 <tr><td>{Localization.getText("Datasource")}</td><td>{foundFeatures?.metadata?.endpoint}</td></tr>
                                 <tr><td>{Localization.getText("Matched")}</td><td>{foundFeatures?.metadata?.numberMatched}</td></tr>
                                 <tr><td>{Localization.getText("Returned")}</td><td>{foundFeatures?.metadata?.numberReturned}</td></tr>
                              </tbody>
                           </table>
                        </GlassInputControl>
                     </>
                     : <></>
               }
            </GlassInputGroup>

         </ADModalBody>
         <ADModalFooter >
            <GlassButton disabled={!foundFeatures} onClick={async () => {
               Persistence.createLayersFromGeoJSON(featureType || "From Conflict", foundFeatures, new Date(), (li: LayerInfo) => { mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: li, zoomToFocus: false } }) });
            }} >
               {Localization.getText("Add as layer")}
            </GlassButton>
            <GlassButton onClick={async () => {
               windowDispatch(closeWindow(window.id))
            }}>
               {Localization.getText("Close")}
            </GlassButton>

         </ADModalFooter>
      </AdvancedDragModal >
   );
}

function removeLayer(map, id) {
   if (map.getLayer(id + '-fill')) {
      map.removeLayer(id + '-fill');
   }
   if (map.getLayer(id + '-outline')) {
      map.removeLayer(id + '-outline');
   }
   if (map.getLayer(id + '-point')) {
      map.removeLayer(id + '-point');
   }
   if (map.getSource(id)) {
      map.removeSource(id);
   }
}

function addFill(map, id, color) {
   if (!map.getSource(id))
      map.addSource(id, {
         type: 'geojson',
         data: {
            type: 'FeatureCollection',
            features: []
         }
      });
   if (!map.getLayer(id + '-fill'))
      map.addLayer({
         id: id + '-fill',
         type: 'fill',
         source: id,
         paint: {
            'fill-color': color,
            'fill-opacity': 0.4
         }
      });
}

function addPoint(map, id, color) {
   if (!map.getSource(id))
      map.addSource(id, {
         type: 'geojson',
         data: {
            type: 'FeatureCollection',
            features: []
         }
      });
   if (!map.getLayer(id + '-point'))
      map.addLayer({
         id: id + '-point',
         type: 'symbol',
         source: id,
         layout: {
            "icon-image": "mit-pin-" + color,
            "icon-anchor": "bottom",
            "icon-size": 1,
            "icon-allow-overlap": true,
            "icon-ignore-placement": true
         },
         filter: ['==', '$type', 'Point']
      });
}



function setGeom(map: any, id: string, geom?: any | null) {
   let source = map.getSource(id) as GeoJSONSource;
   if (source) {
      if (!geom) {
         source.setData({
            type: 'FeatureCollection',
            features: []
         } as any);
         return;
      }
      source.setData(geom as any);
   }
}

function addOutline(map, id, color) {
   if (!map.getSource(id))
      map.addSource(id, {
         type: 'geojson',
         data: {
            type: 'FeatureCollection',
            features: []
         }
      });
   if (!map.getLayer(id + '-outline'))
      map.addLayer({
         id: id + '-outline',
         type: 'line',
         source: id,
         paint: {
            'line-color': color,
            'line-width': 2
         }
      });
}
